import Phaser from 'phaser';
import { TRAY_HEIGHT } from '../../config/constants';
import { UiOverlay } from '../../services/UiOverlay';

type SafeRect = { left: number; top: number; width: number; height: number };
type TrayRect = { x: number; y: number; w: number; h: number };

export class PuzzleLayout {
  /** Safe rect в координатах viewport камеры (DOM → viewport). */
  static computeSafeRect(scene: Phaser.Scene): SafeRect | null {
    const rects = UiOverlay.getPuzzleLayoutRects();
    if (!rects) return null;
    const cam = scene.cameras.main;
    const canvasRect = scene.game.canvas.getBoundingClientRect();
    if (canvasRect.width <= 0 || canvasRect.height <= 0) return null;

    const viewportY = (domY: number) =>
      (domY - canvasRect.top) * (cam.height / canvasRect.height);

    if (rects.boardWrap && rects.boardWrap.width > 0 && rects.boardWrap.height > 0) {
      const boardLeft = (rects.boardWrap.left - canvasRect.left) * (cam.width / canvasRect.width);
      const boardTop = viewportY(rects.boardWrap.top);
      const boardW = rects.boardWrap.width * (cam.width / canvasRect.width);
      const boardH = rects.boardWrap.height * (cam.height / canvasRect.height);
      return {
        left: Math.max(0, boardLeft),
        top: Math.max(0, boardTop),
        width: Math.min(cam.width, boardW),
        height: Math.min(cam.height, boardH),
      };
    }

    const safeTop = viewportY(rects.topbar.bottom);
    const safeBottom = viewportY(rects.tray.top);
    const safeHeight = Math.max(0, safeBottom - safeTop);

    return {
      left: 0,
      top: safeTop,
      width: cam.width,
      height: safeHeight,
    };
  }

  /** Rect лотка в игровых координатах камеры (фиксированно снизу, без DOM). Fallback. */
  private static getFallbackTrayRect(scene: Phaser.Scene): TrayRect {
    const cam = scene.cameras.main;
    const trayH = TRAY_HEIGHT;
    const trayY = cam.height - trayH;
    return { x: 0, y: trayY, w: cam.width, h: trayH };
  }

  /** DOMRect → viewport камеры (canvas scale). */
  private static domRectToViewport(
    scene: Phaser.Scene,
    rect: DOMRect
  ): TrayRect {
    const cam = scene.cameras.main;
    const canvasRect = scene.game.canvas.getBoundingClientRect();
    if (canvasRect.width <= 0 || canvasRect.height <= 0) {
      return { x: 0, y: 0, w: cam.width, h: cam.height };
    }
    const scaleX = cam.width / canvasRect.width;
    const scaleY = cam.height / canvasRect.height;
    return {
      x: (rect.left - canvasRect.left) * scaleX,
      y: (rect.top - canvasRect.top) * scaleY,
      w: rect.width * scaleX,
      h: rect.height * scaleY,
    };
  }

  /** Ограничивает rect лотка видимой областью камеры (DOM может дать x < 0 при letterbox). */
  private static clampTrayRectToCamera(scene: Phaser.Scene, tr: TrayRect): TrayRect {
    const cam = scene.cameras.main;
    const x = Math.max(0, tr.x);
    const maxW = cam.width - x;
    const w = Math.min(tr.w, maxW);
    return { x, y: tr.y, w, h: tr.h };
  }

  /** Базовый rect лотка по DOM или fallback, уже приведённый к viewport и ограниченный камерой. */
  static computeTrayRect(scene: Phaser.Scene): TrayRect {
    const dom = UiOverlay.getTrayScrollDomRect?.() ?? null;
    const base = dom
      ? this.domRectToViewport(scene, dom)
      : this.getFallbackTrayRect(scene);
    return this.clampTrayRectToCamera(scene, base);
  }

  /** Применяет layout превью пазла и текста, не меняя математику. */
  static applyPreviewLayout(
    scene: Phaser.Scene,
    puzzleImage: Phaser.GameObjects.Image | null,
    puzzleText: Phaser.GameObjects.Text | null
  ): void {
    if (!puzzleImage || !puzzleImage.scene) return;
    const safeRect = this.computeSafeRect(scene);
    if (!safeRect) return;

    const cam = scene.cameras.main;
    const cx = cam.width / 2;
    const cy = safeRect.top + safeRect.height / 2; // (safeTop + safeBottom) / 2

    const key = puzzleImage.texture.key;
    const tex = scene.textures.get(key).getSourceImage() as HTMLImageElement;
    const scale = Math.min(
      (safeRect.width * 0.95) / tex.width,
      (safeRect.height * 0.95) / tex.height
    );

    puzzleImage.setOrigin(0.5, 0.5);
    puzzleImage.setPosition(cx, cy);
    puzzleImage.setScale(scale);

    if (puzzleText) {
      puzzleText.setScrollFactor(0);
      puzzleText.setPosition(cx, safeRect.top + safeRect.height - 8);
    }
  }
}

